import { apiFetch } from "./client";
import type {
  ColumnMapping,
  ImportRow,
  ImportRun,
  ImportSource,
  ImportType,
  ManualImportPreview,
  Page,
} from "./types";
import type { ListParams } from "./customers";

export interface ImportSourceInput {
  code: string;
  mailbox_account_id: number | null;
  import_type: ImportType;
  subject_filter: string | null;
  sender_allowlist: string[];
  poll_interval_minutes: number;
  is_enabled: boolean;
}

export interface ColumnMappingInput {
  import_type: ImportType;
  import_source_id: number | null;
  destination_field: string;
  is_required: boolean;
  sort_order?: number;
  aliases: string[];
}

export async function listSources(params: ListParams = {}): Promise<Page<ImportSource>> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  const query = qs.toString();
  return apiFetch(`/import-sources${query ? `?${query}` : ""}`);
}

export async function createSource(body: ImportSourceInput): Promise<ImportSource> {
  return apiFetch("/import-sources", { method: "POST", body: JSON.stringify(body) });
}

export async function updateSource(
  id: number,
  body: Partial<ImportSourceInput>,
): Promise<ImportSource> {
  return apiFetch(`/import-sources/${id}`, { method: "PUT", body: JSON.stringify(body) });
}

export async function deleteSource(id: number): Promise<{ ok: boolean }> {
  return apiFetch(`/import-sources/${id}`, { method: "DELETE" });
}

/** Trigger an immediate mailbox poll for a source. */
export async function pollSource(id: number): Promise<{ runs: number }> {
  return apiFetch(`/import-sources/${id}/poll`, { method: "POST" });
}

/** Upload an attachment and run it through the full import pipeline. */
export async function manualImport(
  sourceId: number,
  file: File,
): Promise<ImportRun> {
  const body = new FormData();
  body.append("file", file);
  return apiFetch(`/import-sources/${sourceId}/manual-import`, {
    method: "POST",
    body,
  });
}

export async function listRuns(params: ListParams & { source_id?: number } = {}): Promise<Page<ImportRun>> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  if (params.source_id !== undefined) qs.set("source_id", String(params.source_id));
  const query = qs.toString();
  return apiFetch(`/import-sources/runs${query ? `?${query}` : ""}`);
}

export async function getRun(runId: number): Promise<ImportRun> {
  return apiFetch(`/import-sources/runs/${runId}`);
}

export async function getRunRows(runId: number): Promise<ImportRow[]> {
  return apiFetch(`/import-sources/runs/${runId}/rows`);
}

export async function replayRun(runId: number): Promise<ImportRun> {
  return apiFetch(`/import-sources/runs/${runId}/replay`, { method: "POST" });
}

export async function discardRow(runId: number, rowId: number): Promise<ImportRow> {
  return apiFetch(`/import-sources/runs/${runId}/rows/${rowId}/discard`, {
    method: "POST",
  });
}

// --- Type-scoped column mappings -------------------------------------------

export async function listMappings(importType: ImportType): Promise<ColumnMapping[]> {
  return apiFetch(`/mappings?import_type=${importType}`);
}

export async function createMapping(
  body: ColumnMappingInput,
): Promise<ColumnMapping> {
  return apiFetch("/mappings", { method: "POST", body: JSON.stringify(body) });
}

export async function updateMapping(
  id: number,
  body: Partial<ColumnMappingInput>,
): Promise<ColumnMapping> {
  return apiFetch(`/mappings/${id}`, { method: "PUT", body: JSON.stringify(body) });
}

export async function deleteMapping(id: number): Promise<{ ok: boolean }> {
  return apiFetch(`/mappings/${id}`, { method: "DELETE" });
}

export async function addMappingAlias(
  id: number,
  sourceHeader: string,
): Promise<ColumnMapping> {
  return apiFetch(`/mappings/${id}/aliases`, {
    method: "POST",
    body: JSON.stringify({ source_header: sourceHeader }),
  });
}

/** Delete a single alias from a mapping. */
export async function deleteMappingAlias(
  mappingId: number,
  aliasId: number,
): Promise<ColumnMapping> {
  return apiFetch(`/mappings/${mappingId}/aliases/${aliasId}`, {
    method: "DELETE",
  });
}

/** Persist a manual field order (list position becomes each mapping's sort_order). */
export async function reorderMappings(ids: number[]): Promise<{ ok: boolean }> {
  return apiFetch("/mappings/reorder", {
    method: "PUT",
    body: JSON.stringify({ ids }),
  });
}

// --- Type-scoped manual import (preview then import) -----------------------

/** Analyse an uploaded file: detect the header row and pre-map columns. */
export async function previewManualImport(
  importType: ImportType,
  file: File,
  headerRow?: number | null,
): Promise<ManualImportPreview> {
  const body = new FormData();
  body.append("file", file);
  if (headerRow !== undefined && headerRow !== null) {
    body.append("header_row", String(headerRow));
  }
  return apiFetch(`/import-types/${importType}/preview`, {
    method: "POST",
    body,
  });
}

/** Run a manual import with the reviewed column mapping. */
export async function runManualImport(
  importType: ImportType,
  file: File,
  mapping: Record<string, string>,
  headerRow?: number | null,
): Promise<ImportRun> {
  const body = new FormData();
  body.append("file", file);
  body.append("mapping", JSON.stringify(mapping));
  if (headerRow !== undefined && headerRow !== null) {
    body.append("header_row", String(headerRow));
  }
  return apiFetch(`/import-types/${importType}/import`, {
    method: "POST",
    body,
  });
}
